import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { apiProductos } from '../../services/Axios';
import Loader from '../utils/loader/Loader';
import {
  FaBell,
  FaExclamationTriangle,
  FaClock,
  FaSearch,
  FaFilter,
  FaBoxes,
  FaCalendarAlt,
} from 'react-icons/fa';

interface Product {
  _id: string;
  nombre: string;
  cantidad?: number;
  peso?: number;
  umbral: number;
  fechaCaducidad?: Date;
  valorVenta: number;
  valorVentaTotal: number;
}

function NotificationList() {
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);
  const [nearExpiryProducts, setNearExpiryProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const [showAllExpiry, setShowAllExpiry] = useState(false);
  const [expiryRange, setExpiryRange] = useState(5);

  const fetchNotifications = useCallback(() => {
    setIsLoading(true);
    apiProductos
      .get('/api/productos/notificaciones', { params: { dias: expiryRange } })
      .then((response) => {
        setLowStockProducts(response.data.lowStockProducts);
        setNearExpiryProducts(response.data.nearExpiryProducts);
      })
      .catch((error) => console.error('Error al obtener productos de notificación:', error))
      .finally(() => setIsLoading(false));
  }, [expiryRange]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const filteredLowStock = useMemo(() => {
    const list = lowStockProducts
      .filter(p => p.nombre.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => (a.cantidad ?? a.peso ?? 0) - (b.cantidad ?? b.peso ?? 0));
    return showAllLowStock ? list : list.slice(0, 5);
  }, [lowStockProducts, searchTerm, showAllLowStock]);

  const filteredNearExpiry = useMemo(() => {
    const list = nearExpiryProducts
      .filter(p => p.nombre.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => new Date(a.fechaCaducidad ?? '').getTime() - new Date(b.fechaCaducidad ?? '').getTime());
    return showAllExpiry ? list : list.slice(0, 5);
  }, [nearExpiryProducts, searchTerm, showAllExpiry]);

  const totalAlerts = lowStockProducts.length + nearExpiryProducts.length;
  const criticalAlerts = lowStockProducts.filter(p => (p.cantidad ?? p.peso ?? 0) === 0).length;

  if (isLoading) return <Loader />;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <FaBell className="text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Centro de Notificaciones</h1>
              <p className="text-orange-100 mt-1">Monitorea productos críticos y alertas de inventario</p>
            </div>
          </div>
          {totalAlerts > 0 && (
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2">
              <span className="text-2xl font-bold">{totalAlerts}</span>
              <p className="text-xs text-orange-100">Alertas activas</p>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Alertas</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalAlerts}</p>
              <p className="text-sm text-gray-500 mt-1">Notificaciones activas</p>
            </div>
            <div className="bg-orange-100 p-4 rounded-full">
              <FaBell className="text-orange-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Stock Bajo</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{lowStockProducts.length}</p>
              <p className="text-sm text-gray-500 mt-1">Productos por debajo del umbral</p>
            </div>
            <div className="bg-yellow-100 p-4 rounded-full">
              <FaExclamationTriangle className="text-yellow-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Próximos a Caducar</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{nearExpiryProducts.length}</p>
              <p className="text-sm text-gray-500 mt-1">Productos que vencen pronto</p>
            </div>
            <div className="bg-red-100 p-4 rounded-full">
              <FaClock className="text-red-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Búsqueda
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre de producto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          {/* Expiry Range Filter */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaCalendarAlt className="text-gray-400" />
            </div>
            <select
              value={expiryRange}
              onChange={(e) => setExpiryRange(Number(e.target.value))}
              className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white"
            >
              <option value={5}>Próximos 5 días</option>
              <option value={10}>Próximos 10 días</option>
              <option value={30}>Próximo mes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Critical Alerts Banner */}
      {criticalAlerts > 0 && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center">
            <FaExclamationTriangle className="text-red-500 text-xl mr-3" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                ¡Atención! {criticalAlerts} producto{criticalAlerts > 1 ? 's' : ''} sin stock disponible
              </p>
              <p className="text-xs text-red-700 mt-1">Se requiere acción inmediata para reponer inventario</p>
            </div>
          </div>
        </div>
      )}

      {/* Low Stock Products Table */}
      <ProductTable
        title="Productos con Stock Bajo"
        icon={<FaExclamationTriangle className="text-yellow-500" />}
        products={filteredLowStock}
        isExpiryTable={false}
        showAll={showAllLowStock}
        onToggleShow={() => setShowAllLowStock(prev => !prev)}
        totalCount={lowStockProducts.length}
        themeColor="yellow"
      />

      {/* Near Expiry Products Table */}
      <ProductTable
        title="Productos Próximos a Caducar"
        icon={<FaClock className="text-red-500" />}
        products={filteredNearExpiry}
        isExpiryTable={true}
        showAll={showAllExpiry}
        onToggleShow={() => setShowAllExpiry(prev => !prev)}
        totalCount={nearExpiryProducts.length}
        themeColor="red"
      />
    </div>
  );
}

const ProductTable = ({ 
  title, 
  icon, 
  products, 
  isExpiryTable, 
  showAll, 
  onToggleShow, 
  totalCount,
  themeColor 
}: {
  title: string;
  icon: React.ReactNode;
  products: Product[];
  isExpiryTable?: boolean;
  showAll: boolean;
  onToggleShow: () => void;
  totalCount: number;
  themeColor: 'yellow' | 'red';
}) => {
  const getBgColor = () => themeColor === 'yellow' ? 'bg-yellow-50' : 'bg-red-50';
  const getBorderColor = () => themeColor === 'yellow' ? 'border-yellow-200' : 'border-red-200';

  return (
    <div className="mb-6">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Table Header */}
        <div className={`${getBgColor()} border-b ${getBorderColor()} px-6 py-4`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${themeColor === 'yellow' ? 'bg-yellow-100' : 'bg-red-100'}`}>
                {icon}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                <p className="text-sm text-gray-600">{totalCount} producto{totalCount !== 1 ? 's' : ''} encontrado{totalCount !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {totalCount > 5 && (
              <button
                onClick={onToggleShow}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
              >
                {showAll ? (
                  <>
                    <i className="fas fa-chevron-up mr-2"></i>
                    Ver menos
                  </>
                ) : (
                  <>
                    <i className="fas fa-chevron-down mr-2"></i>
                    Ver todos ({totalCount})
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Table Content */}
        {products.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <FaBoxes className="mr-2" />
                      Nombre del Producto
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isExpiryTable ? (
                      <div className="flex items-center">
                        <FaCalendarAlt className="mr-2" />
                        Fecha de Caducidad
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <FaBoxes className="mr-2" />
                        Cantidad/Peso
                      </div>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Unitario
                  </th>
                  {!isExpiryTable && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((producto) => {
                  const quantity = producto.cantidad ?? producto.peso ?? 0;
                  const isCritical = quantity === 0;
                  const isLow = quantity > 0 && quantity <= producto.umbral;
                  
                  return (
                    <tr key={producto._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-3 ${
                            isCritical ? 'bg-red-500' : isLow ? 'bg-yellow-500' : 'bg-green-500'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-900">{producto.nombre}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {isExpiryTable ? (
                          <div className="flex items-center">
                            <FaCalendarAlt className="mr-2 text-gray-400" />
                            {new Date(producto.fechaCaducidad!).toLocaleDateString('es-CO', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        ) : (
                          producto.cantidad != null
                            ? `${producto.cantidad} unidades`
                            : `${producto.peso?.toFixed(2)} KG`
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ${producto.valorVentaTotal.toLocaleString('es-CO')}
                      </td>
                      {!isExpiryTable && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isCritical ? (
                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Sin Stock
                            </span>
                          ) : isLow ? (
                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Stock Bajo
                            </span>
                          ) : (
                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Stock Normal
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-2 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaBoxes className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No hay productos en esta categoría</h3>
            <p className="text-gray-500">Todos los productos están en buen estado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationList;